import { type MouseEventHandler, type ReactNode } from 'react';

import { type Roles } from '../../constants';
import {
  TRUNCATE_MAX,
  isTruncatedString,
  truncateString,
} from '../../utils/truncateString';
import { BaseTooltip } from '../BaseTooltip';
import { Button } from '../Button';
import { ButtonContainer, Header, Title } from './styles';

export interface IPageHeaderProps {
  title: string;
  buttonText?: string;
  buttonVisibleRoles?: Roles[];
  userRoles?: string[];
  dataCy?: string;
  additionalActions?: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  themeStyle?: 'admin' | 'default';
  disabledButton?: boolean;
  shouldShowTooltip?: boolean;
}

export const PageHeader = ({
  title,
  buttonText,
  buttonVisibleRoles = undefined,
  userRoles,
  dataCy = 'page-header',
  additionalActions = null,
  onClick,
  themeStyle,
  disabledButton = false,
  shouldShowTooltip = false,
  ...props
}: IPageHeaderProps): JSX.Element => {
  const hasSomeRole = buttonVisibleRoles?.some((role) => {
    return userRoles?.includes(role);
  });

  const truncatedTitle = truncateString(title, TRUNCATE_MAX.PAGE_HEADER);

  return (
    <Header data-cy={dataCy} {...props}>
      {shouldShowTooltip ? (
        <BaseTooltip
          message={title}
          dataCy={`title-tooltip-${title.toLowerCase()}`}
          disabled={!isTruncatedString(title, TRUNCATE_MAX.PAGE_HEADER)}
        >
          <Title data-cy={`title-${title.toLowerCase()}`}>
            {truncatedTitle}
          </Title>
        </BaseTooltip>
      ) : (
        <Title data-cy={`title-${title.toLowerCase()}`}>{title}</Title>
      )}
      {(hasSomeRole ?? buttonVisibleRoles === undefined) && (
        <ButtonContainer data-cy="button-container">
          {additionalActions}
          {buttonText !== undefined && buttonText !== '' && (
            <Button
              dataCy={`button-${buttonText.toLowerCase()}`}
              onClick={onClick}
              type="button"
              styleType={themeStyle !== 'admin' ? 'default' : themeStyle}
              disabled={disabledButton}
            >
              {buttonText}
            </Button>
          )}
        </ButtonContainer>
      )}
    </Header>
  );
};
